import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/api';
import PayrollForm from './PayrollForm';
import PayrollBatchForm from './PayrollBatchForm';
import PayrollList from './PayrollList';
import './PayrollManagement.css';

const PayrollManagement = () => {
  const { hasModuleAccess } = useAuth();
  const hasPayrollAccess = hasModuleAccess('PAYROLLS');
  
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const incompleteEmployees = employees.filter(emp => !emp.cin || !emp.cnssNumber);
  
  // États pour la génération d'état de paie
  const [statementYear, setStatementYear] = useState(new Date().getFullYear());
  const [statementMonth, setStatementMonth] = useState(new Date().getMonth() + 1);
  const [statementFormat, setStatementFormat] = useState('pdf');
  const [generatingStatement, setGeneratingStatement] = useState(false);

  // Actions de masse (batch) par période
  const [batchYear, setBatchYear] = useState(new Date().getFullYear());
  const [batchMonth, setBatchMonth] = useState(new Date().getMonth() + 1);
  const [batchBusy, setBatchBusy] = useState(false);
  
  // États pour la génération du rapport CNSS
  const [cnssReportYear, setCnssReportYear] = useState(new Date().getFullYear());
  const [cnssReportType, setCnssReportType] = useState('quarterly'); // 'quarterly' ou 'annual'
  const [cnssReportQuarter, setCnssReportQuarter] = useState(null);
  const [generatingCnssReport, setGeneratingCnssReport] = useState(false);

  useEffect(() => {
    if (hasPayrollAccess) {
      loadPayrolls();
      loadEmployees();
    }
  }, [hasPayrollAccess]);

  const loadPayrolls = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await payrollService.getAll();
      let data = response.data || [];
      // Trier par période décroissante (année puis mois, plus récent en premier)
      data.sort((a, b) => {
        // Comparer d'abord l'année
        if (a.periodYear !== b.periodYear) {
          return (b.periodYear || 0) - (a.periodYear || 0); // Décroissant
        }
        // Si même année, comparer le mois
        return (b.periodMonth || 0) - (a.periodMonth || 0); // Décroissant
      });
      setPayrolls(data);
      setPage(1);
    } catch (err) {
      setError('Erreur lors du chargement des paies');
      console.error('Error loading payrolls:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeeService.getAll();
      setEmployees(response.data);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const handleCreate = () => {
    setSelectedPayroll(null);
    setSelectedEmployeeId(null);
    setShowForm(true);
  };

  const handleEdit = (payroll) => {
    setSelectedPayroll(payroll);
    setSelectedEmployeeId(payroll.employeeId);
    setShowForm(true);
  };

  const handleSave = () => {
    setShowForm(false);
    setSelectedPayroll(null);
    loadPayrolls();
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedPayroll(null);
    setSelectedEmployeeId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette paie ?')) {
      return;
    }

    try {
      await payrollService.delete(id);
      setError('');
      loadPayrolls();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la suppression';
      setError(errorMessage);
    }
  };

  const handleCalculate = async (id) => {
    try {
      await payrollService.calculate(id);
      setError('');
      loadPayrolls();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du calcul';
      setError(errorMessage);
    }
  };

  const handleValidate = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir valider cette paie ?')) {
      return;
    }

    try {
      await payrollService.validate(id);
      setError('');
      loadPayrolls();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la validation';
      setError(errorMessage);
    }
  };

  const handleMarkAsPaid = async (id) => {
    const sendEmail = window.confirm('Marquer cette paie comme payée et envoyer le bulletin par email ?\n\nCliquez sur OK pour envoyer par email, ou Annuler pour marquer comme payée sans envoyer.');
    
    try {
      await payrollService.markAsPaid(id, sendEmail);
      setError('');
      if (sendEmail) {
        alert('Paie marquée comme payée et bulletin envoyé par email avec succès !');
      } else {
        alert('Paie marquée comme payée avec succès !');
      }
      loadPayrolls();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du marquage';
      setError(errorMessage);
    }
  };

  const handleDownloadPdf = async (id) => {
    try {
      await payrollService.downloadPdf(id);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du téléchargement';
      setError(errorMessage);
    }
  };

  const handleSendEmail = async (id) => {
    if (!window.confirm('Envoyer le bulletin de paie par email à l\'employé ?')) {
      return;
    }

    try {
      await payrollService.sendEmail(id);
      setError('');
      alert('Bulletin de paie envoyé par email avec succès !');
      loadPayrolls();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'envoi';
      setError(errorMessage);
    }
  };

  const handleGenerateStatement = async () => {
    setGeneratingStatement(true);
    setError('');
    try {
      const response = await payrollService.generateMonthlyStatement(
        statementYear,
        statementMonth,
        statementFormat
      );
      
      // Créer un blob et télécharger
      const blob = new Blob([response.data], {
        type: statementFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthStr = String(statementMonth).padStart(2, '0');
      link.download = `etat_paie_${monthStr}_${statementYear}.${statementFormat === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de la génération de l\'état de paie: ' + (err.response?.data?.error || err.message));
      console.error('Error generating statement:', err);
    } finally {
      setGeneratingStatement(false);
    }
  };

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleBatchCalculate = async () => {
    if (!window.confirm(`Calculer toutes les fiches NON calculées pour ${String(batchMonth).padStart(2, '0')}/${batchYear} ?`)) return;
    setBatchBusy(true);
    setError('');
    try {
      const res = await payrollService.batchCalculate(batchYear, batchMonth);
      const attempted = res.data?.attempted ?? 0;
      const succeeded = res.data?.succeeded ?? 0;
      const errs = res.data?.errors || [];
      if (errs.length) {
        alert(`Calcul terminé: ${succeeded}/${attempted} réussi(s).\n\nErreurs:\n- ${errs.join('\n- ')}`);
      } else {
        alert(`Calcul terminé: ${succeeded}/${attempted} réussi(s).`);
      }
      loadPayrolls();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du calcul batch');
    } finally {
      setBatchBusy(false);
    }
  };

  const handleBatchValidate = async () => {
    if (!window.confirm(`Valider toutes les fiches NON validées pour ${String(batchMonth).padStart(2, '0')}/${batchYear} ?`)) return;
    setBatchBusy(true);
    setError('');
    try {
      const res = await payrollService.batchValidate(batchYear, batchMonth);
      const attempted = res.data?.attempted ?? 0;
      const succeeded = res.data?.succeeded ?? 0;
      const errs = res.data?.errors || [];
      if (errs.length) {
        alert(`Validation terminée: ${succeeded}/${attempted} réussi(s).\n\nErreurs:\n- ${errs.join('\n- ')}`);
      } else {
        alert(`Validation terminée: ${succeeded}/${attempted} réussi(s).`);
      }
      loadPayrolls();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la validation batch');
    } finally {
      setBatchBusy(false);
    }
  };

  const handleBatchMarkPaid = async () => {
    const sendEmail = window.confirm(
      `Marquer comme payées toutes les fiches NON payées (VALIDÉES) pour ${String(batchMonth).padStart(2, '0')}/${batchYear} et envoyer les bulletins par email ?\n\nOK = envoyer par email, Annuler = sans email.`
    );
    setBatchBusy(true);
    setError('');
    try {
      const res = await payrollService.batchMarkPaid(batchYear, batchMonth, sendEmail);
      const attempted = res.data?.attempted ?? 0;
      const succeeded = res.data?.succeeded ?? 0;
      const errs = res.data?.errors || [];
      if (errs.length) {
        alert(`Paiement terminé: ${succeeded}/${attempted} réussi(s).\n\nErreurs:\n- ${errs.join('\n- ')}`);
      } else {
        alert(`Paiement terminé: ${succeeded}/${attempted} réussi(s).`);
      }
      loadPayrolls();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du paiement batch');
    } finally {
      setBatchBusy(false);
    }
  };

  const handleBatchPrint = async () => {
    if (!window.confirm(`Télécharger tous les bulletins VALIDÉS/PAYÉS pour ${String(batchMonth).padStart(2, '0')}/${batchYear} ?`)) return;
    setBatchBusy(true);
    setError('');
    try {
      const mode = window.confirm('Souhaitez-vous un seul PDF (fusionné) ?\n\nOK = 1 seul PDF\nAnnuler = ZIP (plusieurs PDFs)') ? 'merged' : 'zip';
      const response = await payrollService.downloadPeriodPdfs(batchYear, batchMonth, mode);
      const ext = mode === 'merged' ? 'pdf' : 'zip';
      const monthStr = String(batchMonth).padStart(2, '0');
      downloadBlob(new Blob([response.data], { type: ext === 'pdf' ? 'application/pdf' : 'application/zip' }),
        `bulletins_paie_${monthStr}_${batchYear}.${ext}`
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du téléchargement batch');
    } finally {
      setBatchBusy(false);
    }
  };

  const handleGenerateCnssReport = async () => {
    setGeneratingCnssReport(true);
    setError('');
    try {
      let response;
      let fileName;
      
      if (cnssReportType === 'quarterly' && cnssReportQuarter) {
        response = await payrollService.generateQuarterlyCnssReport(cnssReportYear, cnssReportQuarter);
        fileName = `rapport_cnss_T${cnssReportQuarter}_${cnssReportYear}.pdf`;
      } else if (cnssReportType === 'annual') {
        response = await payrollService.generateAnnualCnssReport(cnssReportYear);
        fileName = `rapport_cnss_${cnssReportYear}.pdf`;
      } else {
        setError('Veuillez sélectionner un trimestre');
        setGeneratingCnssReport(false);
        return;
      }
      
      // Créer un blob et télécharger
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de la génération du rapport CNSS: ' + (err.response?.data?.error || err.message));
      console.error('Error generating CNSS report:', err);
    } finally {
      setGeneratingCnssReport(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.nom} ${employee.prenom}` : '';
  };

  const filteredPayrolls = selectedEmployeeId
    ? payrolls.filter((p) => p.employeeId === selectedEmployeeId)
    : payrolls;

  const totalPages = Math.max(1, Math.ceil(filteredPayrolls.length / pageSize));
  const displayedPayrolls = filteredPayrolls.slice((page - 1) * pageSize, page * pageSize);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const filteredEmployees = employees.filter((emp) => {
    if (!employeeSearchQuery) return true;
    const q = employeeSearchQuery.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(q) || matricule.includes(q) || cin.includes(q) || cnss.includes(q);
  });

  const handleEmployeeSelect = (employee) => {
    if (employee) {
      setSelectedEmployeeId(employee.id);
      setEmployeeSearchQuery(`${employee.nom} ${employee.prenom}`);
    } else {
      setSelectedEmployeeId(null);
      setEmployeeSearchQuery('');
    }
    setShowEmployeeDropdown(false);
    setPage(1);
  };

  if (!hasPayrollAccess) {
    return (
      <div className="payroll-management">
        <div className="error-banner">
          Accès refusé. Vous n'avez pas les permissions nécessaires pour accéder à cette section.
        </div>
      </div>
    );
  }

  return (
    <div className="payroll-management">
      <div className="payroll-header">
        <h1>Gestion de la Paie</h1>
        <div className="header-actions">
          <button onClick={handleCreate} className="btn btn-primary">
            + Nouvelle paie
          </button>
        </div>
      </div>

      {/* Section génération d'état de paie - dans l'en-tête */}
      <div className="statement-generation-section">
        <div className="statement-controls">
          <div className="form-group">
            <label>Année :</label>
            <input
              type="number"
              value={statementYear}
              onChange={(e) => setStatementYear(parseInt(e.target.value))}
              min="2020"
              max="2100"
            />
          </div>
          <div className="form-group">
            <label>Mois :</label>
            <select
              value={statementMonth}
              onChange={(e) => setStatementMonth(parseInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleString('fr-FR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Format :</label>
            <select
              value={statementFormat}
              onChange={(e) => setStatementFormat(e.target.value)}
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
          </div>
        </div>
        <div className="statement-button-container">
          <button
            className="btn btn-primary"
            onClick={handleGenerateStatement}
            disabled={generatingStatement}
          >
            {generatingStatement ? 'Génération...' : '📄 Générer l\'état de paie'}
          </button>
        </div>
      </div>

      

      {/* Section rapport CNSS */}
      <div className="statement-generation-section">
        <div className="statement-header">
          <h3>Rapport CNSS</h3>
        </div>
        <div className="statement-controls">
          <div className="form-group">
            <label>Type :</label>
            <select
              value={cnssReportType}
              onChange={(e) => {
                setCnssReportType(e.target.value);
                if (e.target.value === 'annual') {
                  setCnssReportQuarter(null);
                }
              }}
            >
              <option value="quarterly">Trimestre</option>
              <option value="annual">Année</option>
            </select>
          </div>
          <div className="form-group">
            <label>Année :</label>
            <input
              type="number"
              value={cnssReportYear}
              onChange={(e) => setCnssReportYear(parseInt(e.target.value))}
              min="2020"
              max="2100"
            />
          </div>
          {cnssReportType === 'quarterly' && (
            <div className="form-group">
              <label>Trimestre :</label>
              <select
                value={cnssReportQuarter || ''}
                onChange={(e) => setCnssReportQuarter(parseInt(e.target.value))}
              >
                <option value="">--</option>
                <option value="1">T1 (Janvier - Mars)</option>
                <option value="2">T2 (Avril - Juin)</option>
                <option value="3">T3 (Juillet - Septembre)</option>
                <option value="4">T4 (Octobre - Décembre)</option>
              </select>
            </div>
          )}
        </div>
        <div className="statement-button-container">
          <button
            className="btn btn-primary"
            onClick={handleGenerateCnssReport}
            disabled={generatingCnssReport || (cnssReportType === 'quarterly' && !cnssReportQuarter)}
          >
            {generatingCnssReport ? 'Génération...' : '📋 Générer le rapport CNSS'}
          </button>
        </div>
      </div>
      {/* Actions de masse (batch) par période */}
      <div className="statement-generation-section">
        <div className="statement-header">
          <h3>Actions de masse (période)</h3>
        </div>
        <div className="statement-controls">
          <div className="form-group">
            <label>Année :</label>
            <input
              type="number"
              value={batchYear}
              onChange={(e) => setBatchYear(parseInt(e.target.value))}
              min="2020"
              max="2100"
              disabled={batchBusy}
            />
          </div>
          <div className="form-group">
            <label>Mois :</label>
            <select
              value={batchMonth}
              onChange={(e) => setBatchMonth(parseInt(e.target.value))}
              disabled={batchBusy}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleString('fr-FR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="statement-button-container" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleBatchCalculate} disabled={batchBusy}>
            {batchBusy ? '...' : '🧮 Calculer tous'}
          </button>
          <button className="btn btn-primary" onClick={handleBatchValidate} disabled={batchBusy}>
            {batchBusy ? '...' : '✓ Valider tous'}
          </button>
          <button className="btn btn-primary" onClick={handleBatchPrint} disabled={batchBusy}>
            {batchBusy ? '...' : '🖨️ Imprimer tous'}
          </button>
          <button className="btn btn-primary" onClick={handleBatchMarkPaid} disabled={batchBusy}>
            {batchBusy ? '...' : '💰 Payer tous'}
          </button>
        </div>
      </div>
      {incompleteEmployees.length > 0 && (
        <div className="warning-banner">
          <strong>{incompleteEmployees.length} employé(s)</strong> n'ont pas encore de CIN/CNSS. Complétez leurs fiches avant de lancer un calcul légal de paie.
        </div>
      )}

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group employee-filter">
            <label>Employé</label>
            <div className="employee-select-wrapper">
              <input
                type="text"
                className="employee-search-input"
                placeholder="Rechercher un employé..."
                value={employeeSearchQuery}
                onChange={(e) => {
                  setEmployeeSearchQuery(e.target.value);
                  setShowEmployeeDropdown(true);
                  if (!e.target.value) {
                    setSelectedEmployeeId(null);
                  }
                }}
                onFocus={() => setShowEmployeeDropdown(true)}
                onBlur={() => setTimeout(() => setShowEmployeeDropdown(false), 150)}
              />
              {selectedEmployeeId && (
                <button
                  className="clear-employee-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEmployeeSelect(null);
                  }}
                  title="Effacer"
                >
                  ×
                </button>
              )}
              {showEmployeeDropdown && filteredEmployees.length > 0 && (
                <div
                  className="employee-dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {filteredEmployees.slice(0, 10).map((emp) => (
                    <div
                      key={emp.id}
                      className={`dropdown-item ${selectedEmployeeId === emp.id ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEmployeeSelect(emp);
                      }}
                    >
                      <div className="employee-option">
                        <span className="employee-name">{emp.nom} {emp.prenom}</span>
                        {emp.matricule && <span className="employee-matricule">{emp.matricule}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {(selectedEmployeeId) && (
            <div className="filter-group">
              <button className="btn btn-secondary clear-filters-btn" onClick={() => handleEmployeeSelect(null)}>
                Effacer les filtres
              </button>
            </div>
          )}
        </div>
      </div>

      {showForm ? (
        <div className="form-container">
          {selectedPayroll ? (
            <PayrollForm
              employeeId={selectedEmployeeId}
              employeeName={selectedEmployeeId ? getEmployeeName(selectedEmployeeId) : null}
              employees={employees}
              payroll={selectedPayroll}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <PayrollBatchForm
              employeeId={selectedEmployeeId}
              employeeName={selectedEmployeeId ? getEmployeeName(selectedEmployeeId) : null}
              employees={employees}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}
        </div>
      ) : (
        <>
          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <PayrollList
              payrolls={displayedPayrolls}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCalculate={handleCalculate}
              onValidate={handleValidate}
              onMarkAsPaid={handleMarkAsPaid}
              onDownloadPdf={handleDownloadPdf}
              onSendEmail={handleSendEmail}
            />
          )}
          {!loading && payrolls.length > 0 && (
            <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-secondary" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
                Précédent
              </button>
              <span>Page {page} / {totalPages}</span>
              <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
                Suivant
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayrollManagement;

